import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';

import type { RoleActionDialogState } from './RoleActionConfirmationDialog';

type RoleActionType = Exclude<RoleActionDialogState['type'], 'closed'>;

type RoleMutation = {
  mutateAsync: (params: { roleIds: string[] }) => Promise<unknown>;
};

export type RoleActionToastMessages = {
  archiveSuccess: (name: string) => string;
  restoreSuccess: (name: string) => string;
  bulkArchiveSuccess: (count: number) => string;
  bulkRestoreSuccess: (count: number) => string;
  error: string;
};

type UseRoleActionDialogParams = {
  archiveMutation: RoleMutation;
  restoreMutation: RoleMutation;
  toastMessages: RoleActionToastMessages;
  onSuccess?: (actionType: RoleActionType) => void;
};

export function useRoleActionDialog({
  archiveMutation,
  restoreMutation,
  toastMessages,
  onSuccess,
}: UseRoleActionDialogParams) {
  const toast = useToast();

  const [dialogState, setDialogState] = React.useState<RoleActionDialogState>({
    type: 'closed',
  });

  const [isPending, setIsPending] = React.useState(false);

  const openArchive = React.useCallback(
    (role: { id: string; name: string }) => {
      setDialogState({ type: 'archive', role });
    },
    [],
  );

  const openRestore = React.useCallback(
    (role: { id: string; name: string }) => {
      setDialogState({ type: 'restore', role });
    },
    [],
  );

  const openBulkArchive = React.useCallback((roleIds: string[]) => {
    setDialogState({ type: 'bulkArchive', roleIds });
  }, []);

  const openBulkRestore = React.useCallback((roleIds: string[]) => {
    setDialogState({ type: 'bulkRestore', roleIds });
  }, []);

  const openForRole = React.useCallback(
    (role: { id: string; name: string; status: 'active' | 'archived' }) => {
      if (role.status === 'archived') {
        openRestore(role);
      } else {
        openArchive(role);
      }
    },
    [openArchive, openRestore],
  );

  const cancel = React.useCallback(() => {
    setDialogState({ type: 'closed' });
  }, []);

  const confirm = React.useCallback(async () => {
    if (dialogState.type === 'closed') return;

    const actionType = dialogState.type;
    setIsPending(true);
    try {
      switch (dialogState.type) {
        case 'archive': {
          await archiveMutation.mutateAsync({
            roleIds: [dialogState.role.id],
          });
          toast.success(toastMessages.archiveSuccess(dialogState.role.name));
          break;
        }
        case 'restore': {
          await restoreMutation.mutateAsync({
            roleIds: [dialogState.role.id],
          });
          toast.success(toastMessages.restoreSuccess(dialogState.role.name));
          break;
        }
        case 'bulkArchive': {
          await archiveMutation.mutateAsync({
            roleIds: dialogState.roleIds,
          });
          toast.success(
            toastMessages.bulkArchiveSuccess(dialogState.roleIds.length),
          );
          break;
        }
        case 'bulkRestore': {
          await restoreMutation.mutateAsync({
            roleIds: dialogState.roleIds,
          });
          toast.success(
            toastMessages.bulkRestoreSuccess(dialogState.roleIds.length),
          );
          break;
        }
      }
      setDialogState({ type: 'closed' });
      onSuccess?.(actionType);
    } catch (_err) {
      toast.error(toastMessages.error);
    } finally {
      setIsPending(false);
    }
  }, [
    dialogState,
    archiveMutation,
    restoreMutation,
    toast,
    toastMessages,
    onSuccess,
  ]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open && !isPending) {
        setDialogState({ type: 'closed' });
      }
    },
    [isPending],
  );

  return {
    dialogState,
    isPending,
    openArchive,
    openRestore,
    openBulkArchive,
    openBulkRestore,
    openForRole,
    confirm,
    cancel,
    handleOpenChange,
  };
}
